import * as THREE from 'three';
import { latLonToVec3 } from '../geo/latlon';
import { GLOBE_RADIUS } from '../core/globe';
import { isMobileDevice } from '../textures';

/**
 * Marker primitive — circular dot at a lat/lon on the globe.
 *
 * Phase 1 scope: one shape (circle), fill-only (no stroke), world-unit size.
 * Style variety, screen-pixel mode, stroke/pin/ring shapes, and marker
 * click/hover interaction come in follow-up commits. The MarkerManager API
 * and layer system here is the primitive every future marker type will
 * consume.
 *
 * Layer grouping: each marker belongs to a named layer (default `'default'`).
 * Visibility and clearing operate on layers, so game modes can show/hide
 * whole marker groups without tracking individual IDs.
 */
export interface MarkerBorder {
  /** CSS color or hex string for the border ring. */
  color: string;
  /**
   * Ring thickness as a fraction of the dot's radius. 0 = no border,
   * 0.08 = subtle, 0.2 = thick. Proportional (not screen-pixel) because
   * sprites scale with zoom — see engine-backlog § screen-pixel marker mode.
   */
  widthRatio: number;
}

/**
 * Marker visual mode.
 *
 * - `'sprite'` — flat camera-facing dot drawn from a canvas texture (the
 *   default). Always faces the camera so it stays the same shape from any
 *   angle. Best for clustered or always-visible markers (city/country dots).
 *
 * - `'pin'` — compound mesh: a tiny 3D sphere center dot + a tangent ring
 *   lying on the globe surface. The ring foreshortens naturally at the
 *   horizon (it's a flat mesh oriented outward, not a sprite), so the
 *   pin reads as part of the surface. Best for "where you clicked" /
 *   "where the answer is" anchor markers in distance-based modes.
 */
export type MarkerShape = 'sprite' | 'pin';

export interface MarkerStyle {
  /**
   * Visual mode. Default `'sprite'` for back-compat with the original
   * camera-facing markers.
   */
  shape?: MarkerShape;
  /** CSS color or hex string for the fill. */
  fill: string;
  /**
   * World-unit diameter. Globe radius is 1, so a typical dot is 0.012–0.02.
   * Sprite mode: this is the visible diameter at default zoom.
   * Pin mode: this is the OUTER RING outer diameter; the inner dot is a
   * fraction of this (see `pinDotRatio`).
   */
  size: number;
  /**
   * Pin mode only — inner-dot diameter as a fraction of `size`. Default
   * 0.41 (matches the prototype's 0.0035/0.0085 dot/ring proportion).
   * Smaller values make the dot more like a target reticle.
   */
  pinDotRatio?: number;
  /**
   * Pin mode only — ring inner radius as a fraction of `size / 2` (the
   * outer radius). Default 0.65, leaving a hole between the inner dot
   * and the ring stroke.
   */
  pinRingInnerRatio?: number;
  /**
   * Pin mode only — ring opacity. Default 0.7 (matches prototype). Lower
   * for a subtler halo, higher for a stronger anchor.
   */
  pinRingOpacity?: number;
  /** Sprite mode only — optional subtle border around the fill. */
  border?: MarkerBorder;
  /** Fill color when hovered. Falls back to `fill`. */
  hoverFill?: string;
  /**
   * Size multiplier when hovered. Defaults to 1.0 (no size change) —
   * scaling mid-hover moves the click target and can occlude neighbors in
   * dense clusters (Caribbean). Color alone is the preferred hover cue.
   * Bump above 1 only for "callout" use cases like tutorial highlights.
   */
  hoverScale?: number;
  /** Sprite mode only — border when hovered. Falls back to `border`. */
  hoverBorder?: MarkerBorder;
}

export interface MarkerOptions {
  /** Unique ID for the marker. Re-adding the same ID replaces the previous. */
  id: string;
  /** Geographic position. */
  position: { lat: number; lon: number };
  /** Visual style. */
  style: MarkerStyle;
  /** Layer name for grouping. Defaults to `'default'`. */
  layer?: string;
  /** Caller-defined payload returned in hover/click events (future). */
  data?: unknown;
}

interface StoredSpriteMarker {
  kind: 'sprite';
  options: MarkerOptions;
  sprite: THREE.Sprite;
  baseMaterial: THREE.SpriteMaterial;
  hoverMaterial: THREE.SpriteMaterial;
  baseScale: number;
  hoverScale: number;
}

interface StoredPinMarker {
  kind: 'pin';
  options: MarkerOptions;
  /**
   * Compound-mesh container. Positioned at the marker's world coords and
   * oriented so its local +Z points outward from the globe center, which
   * makes the child ring lie tangent to the surface and the dot symmetric.
   */
  group: THREE.Group;
  dot: THREE.Mesh;
  ring: THREE.Mesh;
  baseDotMaterial: THREE.MeshBasicMaterial;
  hoverDotMaterial: THREE.MeshBasicMaterial;
  baseRingMaterial: THREE.MeshBasicMaterial;
  hoverRingMaterial: THREE.MeshBasicMaterial;
  baseScale: number;
  hoverScale: number;
}

type StoredMarker = StoredSpriteMarker | StoredPinMarker;

/** Default proportions for pin markers — match the prototype. */
const PIN_DOT_RATIO_DEFAULT = 0.41;
const PIN_RING_INNER_RATIO_DEFAULT = 0.65;
const PIN_RING_OPACITY_DEFAULT = 0.7;

/**
 * Sort priority for the markers group + every layer sub-group. Sits above
 * borders (renderOrder 2) and fills (renderOrder 1) so dots always draw on
 * top regardless of layer-toggle order. Three.js's transparent sort uses a
 * Group's `renderOrder` as the `groupOrder` key for its descendants — and
 * RESETS it every time it descends into another Group — so this constant
 * must be applied to BOTH the root group and each layer sub-group.
 */
const MARKER_GROUP_RENDER_ORDER = 3;

/**
 * Touch-device size multiplier applied to every marker's `style.size`.
 * Sprites and pins both render and hit-test against this scaled value
 * so the visible radius matches the touchable radius. Desktop = 1.0
 * (no change); touch = 1.35 — calibrated so finger-occluded dots stay
 * visible and tap targets cross the ~44 CSS-px Apple/Material guideline
 * at typical zoom.
 */
const TOUCH_SIZE_SCALE = 1.35;

/**
 * Marker system. One per MapEngine; exposed as `engine.markers`.
 */
export class MarkerManager {
  private readonly root: THREE.Group;
  private readonly layerGroups = new Map<string, THREE.Group>();
  private readonly markers = new Map<string, StoredMarker>();
  // Cache textures + materials by style hash so same-style markers share GPU resources.
  private readonly textureCache = new Map<string, THREE.CanvasTexture>();
  private readonly materialCache = new Map<string, THREE.SpriteMaterial>();
  private hoveredId: string | null = null;
  /** Per-instance size multiplier — 1 on pointer devices, TOUCH_SIZE_SCALE on touch. */
  private readonly sizeScale: number;

  constructor(parent: THREE.Group) {
    // Markers render just above the globe surface to avoid z-fighting with
    // the texture sphere. Using 1.004 (slightly above the 1.001 borders layer)
    // so dots stack cleanly over borders when both are on.
    this.root = new THREE.Group();
    this.root.name = 'markers';
    // renderOrder on a Group becomes `groupOrder` in Three.js's transparent
    // sort, which trumps every child's per-mesh renderOrder. Setting it
    // above borders (2) and fills (1) guarantees marker dots draw on top
    // regardless of layer state. The same value MUST be set on every layer
    // sub-group too (see getOrCreateLayer) — Three.js resets groupOrder
    // every time it descends into a Group, so a sub-group with default 0
    // would override the root's 3 for its child sprites.
    this.root.renderOrder = MARKER_GROUP_RENDER_ORDER;
    parent.add(this.root);
    this.sizeScale = isMobileDevice() ? TOUCH_SIZE_SCALE : 1;
  }

  /**
   * Effective marker diameter after touch-device scaling. Single helper
   * keeps the sprite renderer, pin renderer, AND the hit-tester in lockstep
   * — the visible disc and the touch target stay the same shape.
   */
  private effectiveSize(rawSize: number): number {
    return rawSize * this.sizeScale;
  }

  /**
   * Add a marker or replace an existing one with the same id. Branches on
   * `style.shape`: sprite (camera-facing canvas dot, default) vs pin
   * (3D dot + tangent ring, surface-anchored). Cheap to call repeatedly.
   */
  add(options: MarkerOptions): void {
    // If same ID already registered, remove before re-adding.
    if (this.markers.has(options.id)) {
      this.remove(options.id);
    }

    const shape: MarkerShape = options.style.shape ?? 'sprite';
    if (shape === 'pin') this.addPin(options);
    else this.addSprite(options);
  }

  private addSprite(options: MarkerOptions): void {
    const layerName = options.layer ?? 'default';
    const layerGroup = this.getOrCreateLayer(layerName);

    const { style } = options;
    const baseMaterial = this.getOrCreateMaterial(style.fill, style.border);
    const hoverMaterial = this.getOrCreateMaterial(
      style.hoverFill ?? style.fill,
      style.hoverBorder ?? style.border,
    );
    const baseScale = this.effectiveSize(style.size);
    const hoverScale = baseScale * (style.hoverScale ?? 1.0);

    const sprite = new THREE.Sprite(baseMaterial);
    // World-space size. Sprite's scale applies in x/y (the faced quad).
    sprite.scale.set(baseScale, baseScale, 1);

    // Position the sprite just above the globe surface so z-testing against
    // the sphere hides it when it's on the far side from the camera.
    const v = latLonToVec3(options.position.lat, options.position.lon, GLOBE_RADIUS * 1.004);
    sprite.position.set(v.x, v.y, v.z);
    sprite.name = `marker.${options.id}`;
    sprite.userData = { markerId: options.id, data: options.data };

    layerGroup.add(sprite);
    this.markers.set(options.id, {
      kind: 'sprite',
      options,
      sprite,
      baseMaterial,
      hoverMaterial,
      baseScale,
      hoverScale,
    });

    // Preserve hover state if a marker with this ID was the hovered one.
    if (this.hoveredId === options.id) {
      sprite.material = hoverMaterial;
      sprite.scale.set(hoverScale, hoverScale, 1);
      sprite.renderOrder = 1;
    }
  }

  private addPin(options: MarkerOptions): void {
    const layerName = options.layer ?? 'default';
    const layerGroup = this.getOrCreateLayer(layerName);

    const { style } = options;
    const dotRatio = style.pinDotRatio ?? PIN_DOT_RATIO_DEFAULT;
    const ringInnerRatio = style.pinRingInnerRatio ?? PIN_RING_INNER_RATIO_DEFAULT;
    const ringOpacity = style.pinRingOpacity ?? PIN_RING_OPACITY_DEFAULT;

    // Geometry sizing: `size` is the OUTER ring's outer DIAMETER. dot is
    // a fraction of size; ring inner radius is a fraction of the outer.
    const ringOuterRadius = this.effectiveSize(style.size) / 2;
    const ringInnerRadius = ringOuterRadius * ringInnerRatio;
    const dotRadius = ringOuterRadius * dotRatio;

    // Geometry. Tiny segment counts — these are very small on screen.
    const dotGeo = new THREE.SphereGeometry(dotRadius, 16, 16);
    const ringGeo = new THREE.RingGeometry(ringInnerRadius, ringOuterRadius, 32);

    // Materials. Not shared / not cached — pins are dynamic and few
    // (a couple of result pins per round); cache complexity isn't worth it.
    const baseDotMaterial = new THREE.MeshBasicMaterial({
      color: style.fill,
      depthTest: true,
      depthWrite: false,
      transparent: true,
    });
    const hoverDotMaterial = new THREE.MeshBasicMaterial({
      color: style.hoverFill ?? style.fill,
      depthTest: true,
      depthWrite: false,
      transparent: true,
    });
    const baseRingMaterial = new THREE.MeshBasicMaterial({
      color: style.fill,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: ringOpacity,
      depthTest: true,
      depthWrite: false,
    });
    const hoverRingMaterial = new THREE.MeshBasicMaterial({
      color: style.hoverFill ?? style.fill,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: ringOpacity,
      depthTest: true,
      depthWrite: false,
    });

    const dot = new THREE.Mesh(dotGeo, baseDotMaterial);
    const ring = new THREE.Mesh(ringGeo, baseRingMaterial);

    // Group container — positioned at lat/lon, oriented so +Z points
    // outward. Children sit at local origin; the ring (XY plane in local
    // coords) ends up tangent to the globe surface.
    const group = new THREE.Group();
    group.name = `marker.${options.id}`;
    group.userData = { markerId: options.id, data: options.data };
    // Same renderOrder as the markers root and layer sub-groups so child
    // descendants inherit groupOrder = 3 (markers always-on-top contract).
    group.renderOrder = MARKER_GROUP_RENDER_ORDER;

    const v = latLonToVec3(options.position.lat, options.position.lon, GLOBE_RADIUS * 1.004);
    group.position.set(v.x, v.y, v.z);
    // lookAt makes local +Z point AT the target. Target = 2× position so
    // the local +Z axis points outward along the surface normal.
    group.lookAt(v.x * 2, v.y * 2, v.z * 2);

    group.add(dot);
    group.add(ring);
    layerGroup.add(group);

    const baseScale = 1;
    const hoverScale = style.hoverScale ?? 1;

    this.markers.set(options.id, {
      kind: 'pin',
      options,
      group,
      dot,
      ring,
      baseDotMaterial,
      hoverDotMaterial,
      baseRingMaterial,
      hoverRingMaterial,
      baseScale,
      hoverScale,
    });

    if (this.hoveredId === options.id) {
      dot.material = hoverDotMaterial;
      ring.material = hoverRingMaterial;
      group.scale.setScalar(hoverScale);
      group.renderOrder = MARKER_GROUP_RENDER_ORDER + 1;
    }
  }

  /** Remove a single marker by ID. Safe to call on unknown IDs. */
  remove(id: string): void {
    const entry = this.markers.get(id);
    if (!entry) return;
    if (entry.kind === 'sprite') {
      entry.sprite.removeFromParent();
      // Material + texture are cached + shared; don't dispose here.
    } else {
      entry.group.removeFromParent();
      // Pin geometries + materials are NOT shared — dispose to free GPU memory.
      entry.dot.geometry.dispose();
      entry.ring.geometry.dispose();
      entry.baseDotMaterial.dispose();
      entry.hoverDotMaterial.dispose();
      entry.baseRingMaterial.dispose();
      entry.hoverRingMaterial.dispose();
    }
    this.markers.delete(id);
    if (this.hoveredId === id) this.hoveredId = null;
  }

  /**
   * Apply hover state to a single marker (or clear it with `null`).
   * Swaps to the cached hover material(s), bumps scale, and raises
   * renderOrder so the hovered marker wins any overlap — critical for
   * clustered regions (Caribbean, Oceania) where dots sit on top of each
   * other.
   */
  setHovered(id: string | null): void {
    if (id === this.hoveredId) return;

    if (this.hoveredId !== null) {
      const prev = this.markers.get(this.hoveredId);
      if (prev) this.applyHover(prev, false);
    }
    if (id !== null) {
      const next = this.markers.get(id);
      if (next) this.applyHover(next, true);
    }
    this.hoveredId = id;
  }

  /** Switch a single marker to / from its hover state. Branches on shape. */
  private applyHover(entry: StoredMarker, hovered: boolean): void {
    if (entry.kind === 'sprite') {
      entry.sprite.material = hovered ? entry.hoverMaterial : entry.baseMaterial;
      const s = hovered ? entry.hoverScale : entry.baseScale;
      entry.sprite.scale.set(s, s, 1);
      entry.sprite.renderOrder = hovered ? 1 : 0;
    } else {
      entry.dot.material = hovered ? entry.hoverDotMaterial : entry.baseDotMaterial;
      entry.ring.material = hovered ? entry.hoverRingMaterial : entry.baseRingMaterial;
      const s = hovered ? entry.hoverScale : entry.baseScale;
      entry.group.scale.setScalar(s);
      // Bump groupOrder one level so a hovered pin sits above other markers.
      entry.group.renderOrder = MARKER_GROUP_RENDER_ORDER + (hovered ? 1 : 0);
    }
  }

  /** Remove every marker in a layer. Omit `layer` to clear all markers. */
  clear(layer?: string): void {
    if (layer === undefined) {
      for (const id of [...this.markers.keys()]) this.remove(id);
      this.hoveredId = null;
      return;
    }
    for (const [id, entry] of this.markers.entries()) {
      if ((entry.options.layer ?? 'default') === layer) this.remove(id);
    }
  }

  /** Toggle visibility for an entire layer. */
  setLayerVisible(layer: string, visible: boolean): void {
    const group = this.layerGroups.get(layer);
    if (group) group.visible = visible;
  }

  /** Whether the given layer is currently visible. */
  getLayerVisible(layer: string): boolean {
    return this.layerGroups.get(layer)?.visible ?? true;
  }

  /** Marker count, optionally filtered by layer. */
  count(layer?: string): number {
    if (layer === undefined) return this.markers.size;
    let n = 0;
    for (const entry of this.markers.values()) {
      if ((entry.options.layer ?? 'default') === layer) n++;
    }
    return n;
  }

  /**
   * Hit-test: find the nearest visible marker whose rendered pixel disc
   * contains the given screen point. Returns null if the point isn't over
   * any marker.
   *
   * The hit zone equals the marker's ACTUAL visible pixel radius at the
   * current zoom — "what you see is what you click." If dots feel too small
   * to hit reliably at some zoom level, the fix is to bump the style.size,
   * not inflate the hit radius beyond the visible dot.
   *
   * Skips markers whose layer is hidden and markers on the far side of the
   * globe (world position farther from camera than origin).
   */
  findNearestAt(
    clickX: number,
    clickY: number,
    camera: THREE.PerspectiveCamera,
    canvasWidth: number,
    canvasHeight: number,
  ): MarkerOptions | null {
    let bestOptions: MarkerOptions | null = null;
    let bestDist = Infinity;

    const camToCenter = camera.position.length();
    const worldPos = new THREE.Vector3();
    const halfFovTan = Math.tan(((camera.fov * Math.PI) / 180) / 2);

    for (const entry of this.markers.values()) {
      const layerName = entry.options.layer ?? 'default';
      const layer = this.layerGroups.get(layerName);
      if (layer && !layer.visible) continue;

      // World position differs by shape but the test logic is identical —
      // both shapes are anchored at a single world-space point.
      const node = entry.kind === 'sprite' ? entry.sprite : entry.group;
      node.getWorldPosition(worldPos);
      const distFromCam = camera.position.distanceTo(worldPos);
      if (distFromCam > camToCenter) continue;

      // Project to canvas-pixel space.
      const projected = worldPos.clone().project(camera);
      const screenX = (projected.x + 1) * 0.5 * canvasWidth;
      const screenY = (1 - projected.y) * 0.5 * canvasHeight;

      const dx = clickX - screenX;
      const dy = clickY - screenY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // Visible on-screen radius at this camera distance.
      // Standard perspective: screen_px_per_world_unit
      //   = canvasHeight / (2 * tan(fov/2) * distance_from_camera)
      // Both shapes use `style.size` as visible diameter (sprite: literal;
      // pin: outer ring's outer diameter), so the same formula works.
      const pxPerUnit = canvasHeight / (2 * halfFovTan * distFromCam);
      const visibleRadiusPx = (this.effectiveSize(entry.options.style.size) / 2) * pxPerUnit;

      if (dist <= visibleRadiusPx && dist < bestDist) {
        bestDist = dist;
        bestOptions = entry.options;
      }
    }

    return bestOptions;
  }

  /** Dispose all markers + cached GPU resources. Called on engine dispose. */
  dispose(): void {
    // remove() handles both shapes (sprite removeFromParent vs pin
    // removeFromParent + per-pin geometry/material disposal).
    for (const id of [...this.markers.keys()]) this.remove(id);
    this.markers.clear();
    for (const group of this.layerGroups.values()) group.removeFromParent();
    this.layerGroups.clear();
    for (const material of this.materialCache.values()) material.dispose();
    this.materialCache.clear();
    for (const texture of this.textureCache.values()) texture.dispose();
    this.textureCache.clear();
    this.root.removeFromParent();
  }

  // -- internals --

  private getOrCreateLayer(name: string): THREE.Group {
    let group = this.layerGroups.get(name);
    if (group) return group;
    group = new THREE.Group();
    group.name = `markers.${name}`;
    // Mirror the root's renderOrder — without this, descending into the
    // sub-group resets groupOrder to 0 for child sprites and they'd sort
    // BELOW fills (groupOrder 0, renderOrder 1) and borders.
    group.renderOrder = MARKER_GROUP_RENDER_ORDER;
    this.root.add(group);
    this.layerGroups.set(name, group);
    return group;
  }

  private getOrCreateMaterial(fill: string, border?: MarkerBorder): THREE.SpriteMaterial {
    const key = materialKey(fill, border);
    const cached = this.materialCache.get(key);
    if (cached) return cached;

    const texture = this.getOrCreateTexture(fill, border);
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: true,
      depthWrite: false,
    });
    this.materialCache.set(key, material);
    return material;
  }

  private getOrCreateTexture(fill: string, border?: MarkerBorder): THREE.CanvasTexture {
    const key = materialKey(fill, border);
    const cached = this.textureCache.get(key);
    if (cached) return cached;

    // 64px canvas is plenty for a crisp circle at sprite sizes we'll use.
    // Three.js scales the texture to sprite dimensions; the outer edge sits
    // at 95% radius so anti-aliasing doesn't clip against the canvas border.
    const dim = 64;
    const canvas = document.createElement('canvas');
    canvas.width = dim;
    canvas.height = dim;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, dim, dim);
      const cx = dim / 2;
      const cy = dim / 2;
      const outerR = (dim / 2) * 0.95;

      if (border && border.widthRatio > 0) {
        // Two concentric fills — outer ring in border color, inner disc in
        // fill color. Simpler + more predictable AA than stroking.
        const borderPx = outerR * border.widthRatio;
        ctx.beginPath();
        ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
        ctx.fillStyle = border.color;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cx, cy, Math.max(0, outerR - borderPx), 0, Math.PI * 2);
        ctx.fillStyle = fill;
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
        ctx.fillStyle = fill;
        ctx.fill();
      }
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 4;
    this.textureCache.set(key, texture);
    return texture;
  }
}

function materialKey(fill: string, border?: MarkerBorder): string {
  return border && border.widthRatio > 0
    ? `${fill}|${border.color}|${border.widthRatio}`
    : fill;
}
